import CodeMirror from '@github-ui/code-mirror'
import type {SelectionChange} from '@github-ui/code-mirror/extensions/selection-listener'
import {useSafeChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {CopilotTextAttacher} from '@github-ui/copilot-chat/utils/copilot-text-attacher'
import {useSafeChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
// We now interact with the chat manager directly for selection attachments (no event indirection)
import type {ThreadScopedSnippetReference} from '@github-ui/copilot-chat-types'
import {NullMessageId} from '@github-ui/copilot-chat-types'
import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useStableCallback} from '@github-ui/use-stable-callback'
import {
  CodeIcon,
  DownloadIcon,
  EyeIcon,
  FileDiffIcon,
  type IconProps,
  LinkExternalIcon,
  SyncIcon,
} from '@primer/octicons-react'
import {IconButton, Link, SegmentedControl} from '@primer/react'
import debounce from 'lodash-es/debounce'
import {startTransition, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import {usePreviewUrl} from '../../contexts/PreviewUrlContext'
import {useGuessCodeMirrorSettings} from '../../hooks/use-code-mirror-config'
import {formatFilePath, getEffectiveLanguage, parseGitHubUrl, useFileInfo} from '../../utils/content-preview-utils'
import {useHydrateReference} from '../../utils/use-hydrate-reference'
import {
  type File,
  getPreviousVersion,
  type PreviewableContent,
  stripVersionFromId,
  type VersionedItemsMap,
} from './content-preview-types'
import {useContentPreview} from './ContentPreviewContext'
import {DiffView} from './DiffView'
import {DiffViewSettings} from './DiffViewSettings'
import {downloadFile} from './download-file'
import styles from './FilePreview.module.css'
import {HtmlPreview} from './HtmlPreview'
import {MarkdownPreview} from './MarkdownPreview'
import {MermaidPreview} from './MermaidPreview'
import {VersionSelector} from './VersionSelector'

interface FilePreviewProps {
  isPreviewOpening: boolean
  file: File
  onClose: () => void
}

type ViewMode = 'code' | 'preview' | 'diff'
const iconMap: Record<ViewMode, React.FunctionComponent<IconProps>> = {
  code: CodeIcon,
  preview: EyeIcon,
  diff: FileDiffIcon,
}

export function FilePreview({isPreviewOpening, file, onClose}: FilePreviewProps) {
  const {updateItem, items, versionedItems} = useContentPreview()
  const state = useSafeChatState()
  const fileMessage = file.isStreaming
    ? state?.messages[state.messages.length - 1]
    : state?.messages.find(message => message.id === file.messageId)
  const {linesOfCode, isPreviewable: isLanguagePreviewable, size} = useFileInfo(file)
  const isPreviewable = isLanguagePreviewable && file.previewEligible !== false
  useHydrateReference(file, updateItem)

  const fileLang = getEffectiveLanguage(file)
  const previewUrl = usePreviewUrl()
  const initialPreviewIsAvailable = !['html', 'mermaid'].includes(fileLang) || Boolean(previewUrl)
  const defaultMode: ViewMode =
    !file.range &&
    ((file.initialViewMode === 'preview' && isPreviewable && initialPreviewIsAvailable) ||
      (file.initialViewMode === undefined && fileLang === 'markdown'))
      ? 'preview'
      : 'code'
  const fileBaseId = stripVersionFromId(file.id)

  const [viewMode, setViewMode] = useState<ViewMode>(defaultMode)
  const [previewRevision, setPreviewRevision] = useState(0)
  const [mountedViews, setMountedViews] = useState<ReadonlySet<ViewMode>>(() => new Set<ViewMode>([defaultMode]))
  const [isUnifiedDiff, setIsUnifiedDiff] = useState(true)
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)
  const [prevFileBaseId, setPrevFileBaseId] = useState(fileBaseId)
  const [previousSourceRange, setPreviousSourceRange] = useState(file.range)
  const [savedModes] = useState(() => new Map<string, ViewMode>())

  // persists the user's choice per file so switching back restores what they had.
  if (prevFileBaseId !== fileBaseId) {
    savedModes.set(prevFileBaseId, viewMode)
    const restoredMode = savedModes.get(fileBaseId) ?? defaultMode
    setPrevFileBaseId(fileBaseId)
    setViewMode(restoredMode)
    setMountedViews(new Set<ViewMode>([restoredMode]))
  }

  if (previousSourceRange !== file.range) {
    setPreviousSourceRange(file.range)
    if (file.range) {
      setViewMode('code')
      setMountedViews(prev => new Set<ViewMode>([...prev, 'code']))
    }
  }

  if (file.previewEligible === false && viewMode === 'preview') {
    setViewMode('code')
  }

  const handleVersionChange = useCallback((versionLabel: string) => {
    setSelectedVersion(versionLabel)
  }, [])

  const previousVersion = getPreviousVersion<File>(file, items, versionedItems)
  const isDiffable = !!previousVersion

  useEffect(() => {
    if (fileMessage?.error?.type === 'publicCode' || fileMessage?.error?.type === 'filtered') {
      onClose()
    }
  }, [fileMessage?.error, onClose])

  const availableModes: Array<[ViewMode, string]> = [['code', 'Code']]
  let effectiveViewMode: ViewMode = 'code'
  if (isPreviewable) {
    availableModes.unshift(['preview', 'Preview'])
    effectiveViewMode = viewMode === 'preview' ? 'preview' : effectiveViewMode
  }
  if (isDiffable) {
    availableModes.push(['diff', 'Diff'])
    effectiveViewMode = viewMode === 'diff' ? 'diff' : effectiveViewMode
  }

  return (
    <div className={styles.container}>
      <div className={`${styles.toolbar} ${styles.slimLineTopTabs}`}>
        <div className={styles.toolbarLeft}>
          {(isPreviewable || isDiffable) && (
            <SegmentedControl
              aria-label="File view"
              size="small"
              onChange={i => {
                const newViewMode = availableModes[i]?.[0] || 'code'
                sendEvent('dotcom_chat.activate', {target: 'BROWSER_FILE_VIEW_OPTION', option: newViewMode})
                startTransition(() => {
                  setViewMode(newViewMode)
                  setMountedViews(prev => {
                    if (prev.has(newViewMode)) return prev
                    const next = new Set(prev)
                    next.add(newViewMode)
                    return next
                  })
                })
              }}
            >
              {availableModes.map(([mode, label]) => {
                const IconComponent = iconMap[mode]

                return (
                  <SegmentedControl.IconButton
                    key={mode}
                    aria-label={label}
                    icon={<IconComponent size="small" />}
                    selected={viewMode === mode}
                  />
                )
              })}
            </SegmentedControl>
          )}
          <VersionSelector item={file} onVersionSelect={handleVersionChange} />
          <FileLink file={file} />
          {(!file.reference || !('url' in file.reference)) && (
            <p className={styles.toolbarLoC}>
              {linesOfCode} {linesOfCode === 1 ? 'line' : 'lines'} · {size}
            </p>
          )}
        </div>
        <div className={styles.toolbarActions}>
          {effectiveViewMode === 'preview' && fileLang === 'html' && Boolean(previewUrl) && (
            <IconButton
              aria-label="Reload preview page"
              icon={SyncIcon}
              variant="invisible"
              size="small"
              onClick={() => {
                setPreviewRevision(revision => revision + 1)
                sendEvent('dotcom_chat.activate', {target: 'BROWSER_FILE_PREVIEW_RELOAD', mode: 'immersive'})
              }}
            />
          )}
          {file.reference && 'url' in file.reference && (
            <IconButton
              as="a"
              type={undefined} // Removes type: 'button' as it's invalid on anchor elements
              aria-label="Open file in a new tab"
              icon={LinkExternalIcon}
              variant="invisible"
              href={file?.reference?.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            />
          )}
          <CopyToClipboardIconButton
            textToCopy={file.value}
            ariaLabel={'Copy code'}
            onCopy={() => sendEvent('dotcom_chat.activate', {target: 'BROWSER_FILE_COPY', mode: 'immersive'})}
            size="small"
          />
          <IconButton
            aria-label="Download code"
            icon={DownloadIcon}
            variant="invisible"
            onClick={() => {
              const fileName = getFileName(file, selectedVersion)
              downloadFile(new File([file.value], fileName))
              sendEvent('dotcom_chat.activate', {target: 'BROWSER_FILE_DOWNLOAD', mode: 'immersive'})
            }}
            size="small"
          />
          {effectiveViewMode === 'diff' && (
            <DiffViewSettings isUnifiedDiff={isUnifiedDiff} setIsUnifiedDiff={setIsUnifiedDiff} />
          )}
        </div>
      </div>
      <div className={styles.content}>
        {isPreviewable && mountedViews.has('preview') && (
          <div style={{display: effectiveViewMode === 'preview' ? 'contents' : 'none'}}>
            <PreviewComponent
              file={file}
              isVisible={effectiveViewMode === 'preview'}
              previewRevision={previewRevision}
              previewUrl={previewUrl}
            />
          </div>
        )}
        {previousVersion && mountedViews.has('diff') && (
          <div style={{display: effectiveViewMode === 'diff' ? 'contents' : 'none'}}>
            <DiffView left={previousVersion} right={file} view={isUnifiedDiff ? 'unified' : 'split'} />
          </div>
        )}
        <div style={{display: effectiveViewMode === 'code' ? 'contents' : 'none'}}>
          <CodeMirrorView file={file} isPreviewOpening={isPreviewOpening} />
        </div>
      </div>
    </div>
  )
}

function getFileName(file: File, selectedVersion: string | undefined): string {
  const extensionIndex = file.name.lastIndexOf('.')
  if (extensionIndex > 0) {
    const baseName = file.name.substring(0, extensionIndex)
    const extension = file.name.substring(extensionIndex)
    return selectedVersion !== undefined ? `${baseName}_${selectedVersion}${extension}` : file.name
  }
  return selectedVersion !== undefined ? `${file.name}_${selectedVersion}` : file.name
}

const saveChanges = debounce(
  (file: File, fileValue: string, updateItem: (file: File) => void) => {
    const updatedFile = {...file, value: fileValue}
    updateItem(updatedFile)
  },
  500,
  {leading: false, trailing: true},
)

function CodeMirrorView({file, isPreviewOpening}: {file: File; isPreviewOpening: boolean}) {
  const {openItem, updateItem, versionedItems} = useContentPreview()
  const manager = useSafeChatManager()
  const focusNextRenderRef = useRef<boolean>(false)
  const isLatest = isLatestVersion(versionedItems, file)
  const isEditableFileType = !file.readOnly && (!file.reference || file.reference.type === 'thread-scoped-file')
  const isEditable = isEditableFileType && !file.isStreaming && isLatest
  const labelId = useId()
  const codeMirrorOptions = useGuessCodeMirrorSettings(file)
  const sourceRange = useMemo(() => {
    if (!file.range) return undefined
    const lines = file.value.split('\n')
    const start = Math.max(1, Math.min(file.range.start, lines.length))
    const end = Math.max(start, Math.min(file.range.end, lines.length))
    const from = lines.slice(0, start - 1).reduce((offset, line) => offset + line.length + 1, 0)
    const to = from + lines.slice(start - 1, end).join('\n').length
    return {from, to}
  }, [file.range, file.value])
  const [editedValue, setEditedValue] = useState<{value: string; id: string}>()
  const fileValue = editedValue?.id === file.id ? editedValue.value : file.value
  // Force loading state on first render
  const [isLoading, setIsLoading] = useState(true)
  const chatState = useSafeChatState()
  const messageCount = chatState?.messages.length ?? 0
  const currentReferences = chatState?.currentReferences

  // Track last attached selection signature and reference to support replace/remove semantics
  const lastSelectionSigRef = useRef<string>('')
  const lastSelectionRef = useRef<ThreadScopedSnippetReference | null>(null)

  // Build selection handlers and debounce for CodeMirror
  const [selectionAttachmentRange, setSelectionAttachmentRange] = useState<
    {from: number; to: number} | null | undefined
  >(undefined)

  // Clear visual selection when attachment chip is dismissed
  useEffect(() => {
    if (!currentReferences) return

    // Check if there are any thread-scoped-snippet references for this file
    const hasThreadScopedSnippetReference = currentReferences.some(
      ref => ref.type === 'thread-scoped-snippet' && ref.name === file.name,
    )

    if (!hasThreadScopedSnippetReference) {
      // No thread-scoped-snippet references for this file - clear visual selection
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectionAttachmentRange(null)
    }
  }, [currentReferences, file.name])

  const selectionListenerOptions = useMemo(() => {
    const sizeLimit = CopilotTextAttacher.getAttachmentSizeLimit()
    return {
      // Remove debounce for immediate visual feedback
      debounceMs: 0,
      onSelection: ({view, text, fromLine, toLine}: SelectionChange) => {
        if (!text || text.trim() === '') return
        if (!manager) return

        // Snap selection to full line boundaries using CodeMirror's line API
        const firstLine = view.state.doc.line(fromLine)
        const lastLine = view.state.doc.line(toLine)

        const snappedFrom = firstLine.from
        const snappedTo = lastLine.to

        // Extract the snapped text
        const snappedText = view.state.doc.sliceString(snappedFrom, snappedTo)

        const signature = `${file.id}:${snappedFrom}-${snappedTo}:${file.value.length}`
        if (lastSelectionSigRef.current === signature) return

        const bytes = new Blob([snappedText]).size
        if (bytes > sizeLimit) return

        const name = file.name

        const reference: ThreadScopedSnippetReference = {
          type: 'thread-scoped-snippet',
          name,
          snippetText: snappedText,
          language: file.language || 'text',
          range: {
            start: fromLine,
            end: toLine,
          },
        }
        // Replace previous selection reference if it exists; otherwise add
        if (lastSelectionRef.current) {
          manager.replaceReference(lastSelectionRef.current, reference)
        } else {
          manager.addReference(reference, 'immersive-selection')
        }
        lastSelectionRef.current = reference
        setSelectionAttachmentRange({from: snappedFrom, to: snappedTo})
        lastSelectionSigRef.current = signature
      },
      onClear: () => {
        if (!lastSelectionSigRef.current) return
        if (manager && lastSelectionRef.current) {
          manager.removeReference(lastSelectionRef.current)
        }
        lastSelectionSigRef.current = ''
        lastSelectionRef.current = null
        setSelectionAttachmentRange(null)
      },
    }
  }, [file.id, file.name, file.value, file.language, manager])

  // Clear selection attachments when file ID changes
  useEffect(() => {
    if (manager && lastSelectionRef.current) {
      manager.removeReference(lastSelectionRef.current)
    }
    lastSelectionRef.current = null
    lastSelectionSigRef.current = ''
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectionAttachmentRange(null)
  }, [file.id, manager])

  // Cleanup any lingering selection attachment when the file preview is closed/unmounted.
  useEffect(() => {
    return () => {
      if (manager && lastSelectionRef.current) {
        manager.removeReference(lastSelectionRef.current)
      }
      lastSelectionRef.current = null
      lastSelectionSigRef.current = ''
    }
  }, [manager])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (isPreviewOpening) {
      focusNextRenderRef.current = true
    }
  }, [isPreviewOpening])

  const onChange = useStableCallback(
    useCallback(
      (value: string) => {
        if (!isEditable || value === file.value) return
        if (!file.isUserEdited) {
          const newFile: File = {
            ...file,
            messageId: NullMessageId,
            id: `file:${file.name}#${messageCount}`,
            timestamp: new Date(),
            isUserEdited: true,
            value,
          }
          updateItem(newFile)
          openItem(newFile.id)
          setEditedValue({value, id: newFile.id})
        } else {
          // we want to re-render this component though, so as to keep CodeMirror in sync with its props
          setEditedValue({value, id: file.id})
          saveChanges(file, value, updateItem)
        }
      },
      [file, isEditable, messageCount, openItem, updateItem],
    ),
  )

  return (
    <>
      <span id={labelId} className="sr-only">
        {file.name} file contents
      </span>
      <CodeMirror
        ariaLabelledBy={labelId}
        containerClassName={styles.codeMirrorContainer}
        fileName={file.name}
        height="100%"
        isReadOnly={!isEditable}
        spacing={codeMirrorOptions}
        value={isLoading ? '' : fileValue}
        onChange={onChange}
        focusNextRenderRef={focusNextRenderRef}
        placeholder="Loading..."
        hideHelp={false}
        selectionListenerOptions={selectionListenerOptions}
        selectionAttachmentRange={isLoading ? undefined : (selectionAttachmentRange ?? sourceRange ?? null)}
      />
      {isEditableFileType && !isLatest && (
        <div className={styles.footer}>Switch to the latest version to edit this file.</div>
      )}
    </>
  )
}

function PreviewComponent({
  file,
  isVisible,
  previewRevision,
  previewUrl,
}: {
  file: File
  isVisible?: boolean
  previewRevision?: number
  previewUrl: string
}) {
  // Track the file value that was last used for enrichment while visible.
  // This defers the key-based remount (which tears down and rebuilds the
  // enrichment iframe) until the preview is actually visible. Without this,
  // editing code in Code mode triggers a remount while the preview container
  // has display:none, causing the viewscreen iframe to render at zero
  // dimensions and producing a blank/broken preview when switching back.
  const [enrichedValue, setEnrichedValue] = useState(file.value)

  // Adjust enrichedValue during render when the preview becomes visible
  // or when the code changes while visible. This is the React-endorsed
  // pattern for derived state (setState during render with a guard).
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (isVisible !== false && enrichedValue !== file.value) {
    setEnrichedValue(file.value)
  }

  const lang = getEffectiveLanguage(file)
  if (lang === 'markdown') {
    return (
      <div className={styles.previewContainer}>
        <MarkdownPreview content={file} />
      </div>
    )
  } else if (lang === 'html') {
    return (
      <div className={styles.previewContainer}>
        {file.htmlPreview && previewUrl ? (
          file.htmlPreview.render({
            isVisible: isVisible !== false,
            previewRevision: previewRevision ?? 0,
            previewUrl,
          })
        ) : (
          <HtmlPreview file={file} previewRevision={previewRevision} />
        )}
      </div>
    )
  } else if (lang === 'mermaid') {
    // Key the wrapper on enrichedValue so the entire subtree (including any
    // DOM the enrichment script injected) is torn down and rebuilt when
    // the code changes — but only once the preview is visible, so the
    // viewscreen iframe renders at the correct dimensions.
    return (
      <div key={enrichedValue} className={styles.previewContainer}>
        <MermaidPreview code={file.value} isStreaming={file.isStreaming} />
      </div>
    )
  } else {
    return null
  }
}

function isLatestVersion(versionedItems: VersionedItemsMap, file: PreviewableContent) {
  const versions = versionedItems.get(stripVersionFromId(file.id)) ?? []
  return versions[versions.length - 1] === file.id
}

export function FileLink({file}: {file: File}) {
  const reference = file.reference

  if (!reference || reference.type !== 'file' || !reference.url) {
    return null
  }

  // parseGitHubUrl validates URL security
  const repoInfo = parseGitHubUrl(reference.url)

  if (!repoInfo) return null

  const {repoOwner, repoName, filePath} = repoInfo

  return (
    <div className={styles.fileLinkContainer}>
      <Link className={styles.filePath} href={reference.url} rel="noopener noreferrer">
        {formatFilePath(repoOwner, repoName, filePath, reference.ref)}
      </Link>
    </div>
  )
}
